import { authConstants } from "./constants";
import axios from "../helpers/axios";
import swal from "sweetalert";
import {
    refreshAdminSocketAuth,
    disconnectAdminSocket,
} from "../containers/ChatPage/socket";
import { refreshCallsAuth } from "../calls";

const AUTH_PROFILE_SYNC = "AUTH_PROFILE_SYNC";

const getErrorMessage = (error, fallback = "Ошибка запроса") => {
    return error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback;
};

const syncAdminProfile = (dispatch, user) => {
    if (!user || typeof user !== "object") return null;

    let storedUser = {};

    try {
        storedUser = JSON.parse(localStorage.getItem("user") || "{}") || {};
    } catch (_error) {
        storedUser = {};
    }

    const mergedUser = {
        ...storedUser,
        ...user,
    };

    localStorage.setItem("user", JSON.stringify(mergedUser));

    dispatch({
        type: AUTH_PROFILE_SYNC,
        payload: {
            user: mergedUser,
        },
    });

    return mergedUser;
};

export const login = (user) => {
    return async (dispatch) => {
        dispatch({ type: authConstants.LOGIN_REQUEST });

        try {
            const res = await axios.post("/admin/signin", { ...user });

            if (res.status === 200) {
                const { token, user: loggedUser } = res.data;

                localStorage.setItem("token", token);
                localStorage.setItem("user", JSON.stringify(loggedUser));
                refreshAdminSocketAuth({ forceReconnect: true });
                refreshCallsAuth();

                dispatch({
                    type: authConstants.LOGIN_SUCCESS,
                    payload: {
                        token,
                        user: loggedUser,
                    },
                });

                swal({
                    title: `Приветствуем вас ${loggedUser.firstName}`,
                    text: "Добавить новый товар или может категорию?",
                    icon: "success",
                });
            }
        } catch (error) {
            dispatch({
                type: authConstants.LOGIN_FAILURE,
                payload: {
                    message: getErrorMessage(error, "Не удалось войти"),
                },
            });
        }
    };
};

export const isUserLoggedIn = () => {
    return async (dispatch) => {
        const token = localStorage.getItem("token");

        if (token) {
            let user = {};

            try {
                user = JSON.parse(localStorage.getItem("user") || "{}") || {};
            } catch (_error) {
                user = {};
            }

            refreshAdminSocketAuth();
            refreshCallsAuth();

            dispatch({
                type: authConstants.LOGIN_SUCCESS,
                payload: {
                    token,
                    user,
                },
            });
        } else {
            dispatch({
                type: authConstants.NEED_TO_LOGIN,
                payload: { message: "Нужно войти в аккаунт" },
            });
        }
    };
};

export const refreshCurrentAdminUser = () => {
    return async (dispatch) => {
        try {
            const res = await axios.get("/auth/me");

            if (res.status === 200 && res.data?.user) {
                const user = syncAdminProfile(dispatch, res.data.user);

                return {
                    ok: true,
                    user,
                };
            }

            return {
                ok: false,
                message: "Не удалось обновить профиль",
            };
        } catch (error) {
            return {
                ok: false,
                message: getErrorMessage(error, "Не удалось обновить профиль"),
            };
        }
    };
};

export const updateCurrentAdminProfile = (section, fields = {}) => {
    return async (dispatch) => {
        try {
            const res = await axios.patch("/user/profile", {
                section,
                fields,
                source: "admin",
            });

            if (res.status === 200) {
                const user = res.data?.user
                    ? syncAdminProfile(dispatch, res.data.user)
                    : null;

                return {
                    ok: true,
                    ...res.data,
                    user,
                };
            }

            return {
                ok: false,
                message: res.data?.message || "Ошибка обновления профиля",
            };
        } catch (error) {
            return {
                ok: false,
                message: getErrorMessage(error, "Ошибка обновления профиля"),
            };
        }
    };
};

export const sendCurrentAdminEmailVerification = () => {
    return async () => {
        try {
            const res = await axios.post("/user/email-verification/send");

            return {
                ok: res.status === 200,
                message: res.data?.message || "Код подтверждения отправлен на email",
                secondsLeft: res.data?.secondsLeft || 0,
                user: res.data?.user || null,
            };
        } catch (error) {
            return {
                ok: false,
                message: getErrorMessage(error, "Ошибка отправки кода подтверждения"),
                secondsLeft: error?.response?.data?.secondsLeft || 0,
            };
        }
    };
};

export const confirmCurrentAdminEmailVerification = (code) => {
    return async (dispatch) => {
        try {
            const res = await axios.post("/user/email-verification/confirm", {
                code: String(code || "").trim(),
            });

            if (res.status === 200) {
                const user = res.data?.user
                    ? syncAdminProfile(dispatch, res.data.user)
                    : null;

                return {
                    ok: true,
                    message: res.data?.message || "Email успешно подтверждён",
                    user,
                };
            }

            return {
                ok: false,
                message: res.data?.message || "Ошибка подтверждения email",
            };
        } catch (error) {
            return {
                ok: false,
                message: getErrorMessage(error, "Ошибка подтверждения email"),
                attemptsLeft: error?.response?.data?.attemptsLeft,
            };
        }
    };
};

export const requestCurrentAdminEmailChange = (email) => {
    return async () => {
        try {
            const res = await axios.post("/user/email-change/request", {
                email: String(email || "").trim().toLowerCase(),
            });

            return {
                ok: res.status === 200,
                message: res.data?.message || "Код отправлен на новый email",
                secondsLeft: res.data?.secondsLeft || 0,
            };
        } catch (error) {
            return {
                ok: false,
                message: getErrorMessage(error, "Ошибка отправки кода"),
                secondsLeft: error?.response?.data?.secondsLeft || 0,
            };
        }
    };
};

export const confirmCurrentAdminEmailChange = (code) => {
    return async (dispatch) => {
        try {
            const res = await axios.post("/user/email-change/confirm", {
                code: String(code || "").trim(),
            });

            if (res.status === 200) {
                const user = res.data?.user
                    ? syncAdminProfile(dispatch, res.data.user)
                    : null;

                return {
                    ok: true,
                    message: res.data?.message || "Email успешно изменён",
                    user,
                };
            }

            return {
                ok: false,
                message: res.data?.message || "Ошибка подтверждения email",
            };
        } catch (error) {
            return {
                ok: false,
                message: getErrorMessage(error, "Ошибка подтверждения email"),
                attemptsLeft: error?.response?.data?.attemptsLeft,
            };
        }
    };
};

export const requestCurrentAdminPasswordChange = (payload = {}) => {
    return async () => {
        try {
            const res = await axios.post("/user/change-password/request", {
                currentPassword: payload.currentPassword || "",
                newPassword: payload.newPassword || "",
                confirmPassword: payload.confirmPassword || "",
            });

            return {
                ok: res.status === 200,
                message: res.data?.message || "Код подтверждения отправлен на email",
                secondsLeft: res.data?.secondsLeft || 0,
            };
        } catch (error) {
            return {
                ok: false,
                message: getErrorMessage(error, "Ошибка отправки кода"),
                secondsLeft: error?.response?.data?.secondsLeft || 0,
            };
        }
    };
};

export const confirmCurrentAdminPasswordChange = (code) => {
    return async () => {
        try {
            const res = await axios.post("/user/change-password/confirm", {
                code: String(code || "").trim(),
            });

            return {
                ok: res.status === 200,
                message: res.data?.message || "Пароль успешно изменён",
            };
        } catch (error) {
            return {
                ok: false,
                message: getErrorMessage(error, "Ошибка подтверждения смены пароля"),
                attemptsLeft: error?.response?.data?.attemptsLeft,
            };
        }
    };
};

export const signout = () => {
    return async (dispatch) => {
        dispatch({ type: authConstants.LOGOUT_REQUEST });

        try {
            const res = await axios.post("/admin/signout");

            if (res.status === 200) {
                localStorage.clear();
                disconnectAdminSocket();
                refreshCallsAuth();
                dispatch({ type: authConstants.LOGOUT_SUCCESS, payload: res.data });
                return;
            }

            dispatch({
                type: authConstants.LOGOUT_FAILURE,
                payload: { error: res.data?.error },
            });
        } catch (error) {
            dispatch({
                type: authConstants.LOGOUT_FAILURE,
                payload: { error: getErrorMessage(error, "Ошибка выхода") },
            });
        }
    };
};
